import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, ArrowUpRight, Lock, LogIn } from "lucide-react";
import Tm from "./ow/Tm";
import { CALENDLY_URL, LINKEDIN_URL, EMAIL, LOCATION, COMPANY } from "../lib/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-brand-700 text-white"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white text-brand-700 flex items-center justify-center font-serif text-xl">
                H
              </div>
              <div className="leading-tight">
                <div className="font-serif text-xl">Holder Hospitality</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300">
                  Consulting
                </div>
              </div>
            </div>
            <p className="text-brand-100/90 text-base leading-relaxed max-w-md">
              Fractional executive leadership for restaurant, hospitality, and food service
              operators. We are operators first. We build the systems, recruit the leaders,
              and do the work alongside your team.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-5 py-3 text-sm font-medium"
              data-testid="footer-book-call-btn"
            >
              Book a Call <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Sitemap */}
          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300 mb-5">
              Sitemap
            </div>
            <ul className="space-y-3 text-brand-100/90">
              {[
                { to: "/", label: "Home" },
                { to: "/services", label: "Services" },
                { to: "/services/operator-walk", label: "The Operator Walk" },
                { to: "/about", label: "About" },
                { to: "/blog", label: "Blog" },
                { to: "/assessment", label: "Free Assessment" },
                { to: "/contact", label: "Contact" },
                { to: "/careers", label: "Careers" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="hover:text-white transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                    {l.to === "/services/operator-walk" && <Tm />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300 mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-brand-100/90">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-brand-300 shrink-0" />
                <a
                  href={`mailto:${EMAIL}`}
                  className="hover:text-white transition-colors break-all"
                  data-testid="footer-email"
                >
                  {EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-brand-300 shrink-0" />
                <span data-testid="footer-location">{LOCATION}. Serving clients nationally.</span>
              </li>
              <li className="flex items-start gap-3">
                <Linkedin className="w-4 h-4 mt-1 text-brand-300 shrink-0" />
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                  data-testid="footer-linkedin"
                >
                  linkedin.com/in/crholder
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-brand-300">
          <div>
            &copy; {year} {COMPANY}. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/portal/login"
              className="inline-flex items-center gap-1.5 text-brand-300 hover:text-white transition-colors"
              data-testid="footer-client-login-link"
            >
              <LogIn className="w-3.5 h-3.5" /> Client Login
            </Link>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-brand-300 hover:text-white transition-colors"
              data-testid="footer-admin-link"
            >
              <Lock className="w-3.5 h-3.5" /> Admin
            </Link>
            <span className="font-serif italic text-brand-100/80">
              Operators first. Always.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
